import type { CSSProperties } from 'react'

import {
  BRACKET_COLUMN_GAP,
  BRACKET_COLUMN_WIDTH,
  BRACKET_ROW_GAP,
  BRACKET_ROW_HEIGHT,
  BRACKET_TOP_PADDING,
  CHAMPION_COLUMN_WIDTH,
  CHAMPION_CONNECTOR_OFFSET,
  FINAL_COLUMN_WIDTH,
  HORIZONTAL_FINAL_SLOT_EDGE_OFFSET,
  IMAGE_CENTER_OFFSET,
  IMAGE_SLOT_SIZE,
  REGION_GAP,
  SINGLE_FINAL_COLUMN_WIDTH,
  TEXT_SLOT_HEIGHT,
  TEXT_SLOT_WIDTH,
  VERTICAL_HEADER_CONTENT_GAP,
  VERTICAL_HEADER_RAIL_WIDTH,
  VERTICAL_MIN_BRACKET_WIDTH,
  VERTICAL_SLOT_EDGE_OFFSET,
  VERTICAL_STACKED_FINAL_CONNECTOR_GAP,
  getGridSpanHeight,
  getVerticalSlotCenterOffset,
} from './constants'
import type {
  BracketBox,
  BracketDimensions,
  BracketDirection,
  BracketDisplayMode,
  BracketLayout,
  BracketStructure,
  ChampionPlacement,
  ColumnHeader,
  ConnectorLine,
  PositionedMatch,
  RenderedBracketDimensions,
  WinnerSide,
} from './types'

export function computeBracketLayout(
  rounds: readonly (readonly unknown[])[],
  structure: BracketStructure,
  totalSlots: number,
): BracketLayout {
  const roundCount = rounds.length
  if (roundCount === 0) {
    return {
      matchLayout: [],
      columnCount: 0,
      centerColumn: 0,
      maxRegionOffset: 0,
    }
  }

  const baseSlots = structure === 'two-sided' ? totalSlots / 2 : totalSlots
  const baseLayouts = rounds.map((round, roundIndex) =>
    round.map((_, matchIndex) => {
      const isFinalRound = roundIndex === roundCount - 1
      const compactFinal = isFinalRound && structure !== 'single'
      const span = compactFinal
        ? Math.max(1, Math.min(baseSlots, 2))
        : Math.min(2 ** (roundIndex + 1), baseSlots)
      const leftMatches = Math.max(1, baseSlots / span)
      const isLeft = structure === 'single' ? true : matchIndex < leftMatches
      const localIndex =
        structure === 'single'
          ? matchIndex
          : isLeft
            ? matchIndex
            : matchIndex - leftMatches

      let startRow = localIndex * span + 1
      if (compactFinal) {
        startRow = Math.floor((baseSlots - span) / 2) + 1
      }
      const startTop = (startRow - 1) * (BRACKET_ROW_HEIGHT + BRACKET_ROW_GAP)
      const height = span * BRACKET_ROW_HEIGHT + (span - 1) * BRACKET_ROW_GAP
      const centerY = BRACKET_TOP_PADDING + startTop + height / 2

      return { span, startRow, centerY }
    }),
  )

  const maxDistance = roundCount - 1
  const columnCount =
    structure === 'single' ? roundCount : Math.max(1, 2 * maxDistance + 1)
  const centerColumn = structure === 'single' ? 0 : maxDistance

  const regionCount = structure === 'regions' ? (totalSlots >= 16 ? 4 : 2) : 0
  const regionSize = regionCount ? totalSlots / regionCount : 0
  const regionRounds = regionSize ? Math.log2(regionSize) : 0
  const regionsPerSide = regionCount ? regionCount / 2 : 0
  const maxRegionOffset =
    structure === 'regions' ? Math.max(0, regionsPerSide - 1) * REGION_GAP : 0

  const matchLayout: PositionedMatch[][] = baseLayouts.map(
    (round, roundIndex) =>
      round.map((layout, matchIndex) => {
        const segmentSize = 2 ** (roundIndex + 1)
        const segmentStart = matchIndex * segmentSize
        const distance = maxDistance - roundIndex

        let relativeColumn = structure === 'single' ? roundIndex : 0
        if (structure === 'two-sided' || structure === 'regions') {
          const isLeft = segmentStart < totalSlots / 2
          relativeColumn = distance === 0 ? 0 : isLeft ? -distance : distance
        }

        const columnIndex =
          structure === 'single'
            ? relativeColumn + 1
            : relativeColumn + centerColumn + 1

        let translateY = 0
        if (
          structure === 'regions' &&
          roundIndex < regionRounds &&
          regionSize
        ) {
          const regionIndex = Math.floor(segmentStart / regionSize)
          const positionInSide =
            regionsPerSide > 0 ? regionIndex % regionsPerSide : 0
          translateY = positionInSide * REGION_GAP
        }

        return {
          ...layout,
          columnIndex,
          relativeColumn,
          translateY,
          centerY: layout.centerY + translateY,
        }
      }),
  )

  return { matchLayout, columnCount, centerColumn, maxRegionOffset }
}

export function computeBracketDimensions(
  layout: BracketLayout,
  structure: BracketStructure,
  totalSlots: number,
): BracketDimensions {
  const finalColumnIndex =
    structure === 'single' ? layout.columnCount : layout.centerColumn + 1
  const hasChampionColumn = structure === 'single'
  const finalColumnWidth =
    structure === 'single' ? SINGLE_FINAL_COLUMN_WIDTH : FINAL_COLUMN_WIDTH

  const columnWidths: number[] = Array.from(
    { length: layout.columnCount },
    (_, index) => {
      const columnIndex = index + 1
      const isFinal = finalColumnIndex > 0 && columnIndex === finalColumnIndex
      return isFinal ? finalColumnWidth : BRACKET_COLUMN_WIDTH
    },
  )

  if (hasChampionColumn) {
    columnWidths.push(CHAMPION_COLUMN_WIDTH)
  }

  const championColumnIndex = hasChampionColumn ? columnWidths.length : null
  const columnOffsets: number[] = []
  let acc = 0

  columnWidths.forEach((width, index) => {
    columnOffsets[index] = acc
    acc += width
    if (index < columnWidths.length - 1) {
      acc += BRACKET_COLUMN_GAP
    }
  })

  const bracketWidth = columnWidths.reduce(
    (sum, width, index) =>
      sum + width + (index < columnWidths.length - 1 ? BRACKET_COLUMN_GAP : 0),
    0,
  )

  const effectiveSlots = structure === 'two-sided' ? totalSlots / 2 : totalSlots
  const bracketHeight =
    BRACKET_TOP_PADDING +
    effectiveSlots * BRACKET_ROW_HEIGHT +
    (effectiveSlots - 1) * BRACKET_ROW_GAP +
    (structure === 'regions' ? layout.maxRegionOffset : 0)

  return {
    bracketHeight,
    bracketWidth,
    championColumnIndex,
    columnOffsets,
    columnWidths,
    finalColumnIndex,
    finalColumnWidth,
    hasChampionColumn,
  }
}

export function computeRenderedBracketDimensions(
  dimensions: BracketDimensions,
  direction: BracketDirection,
): RenderedBracketDimensions {
  if (direction === 'horizontal') {
    return {
      bracketHeight: dimensions.bracketHeight,
      bracketWidth: dimensions.bracketWidth,
      verticalContentWidth: 0,
      verticalXOffset: 0,
    }
  }

  const verticalContentWidth = Math.max(
    0,
    dimensions.bracketHeight - BRACKET_TOP_PADDING,
  )
  const verticalContentAreaWidth = Math.max(
    VERTICAL_MIN_BRACKET_WIDTH,
    verticalContentWidth,
  )
  const verticalHeaderAreaWidth =
    VERTICAL_HEADER_RAIL_WIDTH + VERTICAL_HEADER_CONTENT_GAP

  return {
    bracketHeight: BRACKET_TOP_PADDING + dimensions.bracketWidth,
    bracketWidth: verticalHeaderAreaWidth + verticalContentAreaWidth,
    verticalContentWidth,
    verticalXOffset:
      verticalHeaderAreaWidth +
      (verticalContentAreaWidth - verticalContentWidth) / 2,
  }
}

export function getMatchBox(
  layout: PositionedMatch,
  columnOffsets: number[],
  columnWidths: number[],
): BracketBox {
  return {
    height: getGridSpanHeight(layout.span),
    left: columnOffsets[layout.columnIndex - 1] ?? 0,
    top:
      BRACKET_TOP_PADDING +
      (layout.startRow - 1) * (BRACKET_ROW_HEIGHT + BRACKET_ROW_GAP) +
      layout.translateY,
    width: columnWidths[layout.columnIndex - 1] ?? BRACKET_COLUMN_WIDTH,
  }
}

export function getChampionBox(
  placement: ChampionPlacement,
  columnOffsets: number[],
  columnWidths: number[],
): BracketBox {
  const span = placement.rowEnd - placement.rowStart

  return {
    height: getGridSpanHeight(span),
    left: columnOffsets[placement.columnIndex - 1] ?? 0,
    top:
      BRACKET_TOP_PADDING +
      (placement.rowStart - 1) * (BRACKET_ROW_HEIGHT + BRACKET_ROW_GAP),
    width: columnWidths[placement.columnIndex - 1] ?? CHAMPION_COLUMN_WIDTH,
  }
}

export function transposeBox(
  box: BracketBox,
  xOffset: number,
  yAxisLength: number,
  reverseY = false,
): BracketBox {
  return {
    height: box.width,
    left: box.top - BRACKET_TOP_PADDING + xOffset,
    top:
      BRACKET_TOP_PADDING +
      (reverseY ? yAxisLength - box.left - box.width : box.left),
    width: box.height,
  }
}

export function toCssBox(box: BracketBox): CSSProperties {
  return {
    height: box.height,
    left: box.left,
    top: box.top,
    width: box.width,
  }
}

function getHorizontalSlotEdgeInset(
  columnWidth: number,
  displayMode: BracketDisplayMode,
) {
  const slotWidth = displayMode === 'text' ? TEXT_SLOT_WIDTH : IMAGE_SLOT_SIZE
  return Math.max(0, (columnWidth - slotWidth) / 2)
}

function getHorizontalFinalSlotEdgeOffset(displayMode: BracketDisplayMode) {
  return displayMode === 'text' ? 0 : HORIZONTAL_FINAL_SLOT_EDGE_OFFSET
}

function getHorizontalSlotCenterY(
  boxTop: number,
  boxHeight: number,
  side: WinnerSide,
  displayMode: BracketDisplayMode,
) {
  const center =
    boxTop +
    boxHeight * (side === 'home' ? 0.25 : 0.75) +
    getVerticalSlotCenterOffset(side)

  return displayMode === 'text' ? center : center - IMAGE_CENTER_OFFSET
}

function getVerticalSlotEdgeOffset(displayMode: BracketDisplayMode) {
  return displayMode === 'text'
    ? TEXT_SLOT_HEIGHT / 2
    : VERTICAL_SLOT_EDGE_OFFSET
}

export function buildConnectors(
  matchLayout: PositionedMatch[][],
  columnOffsets: number[],
  columnWidths: number[],
  structure: BracketStructure,
  displayMode: BracketDisplayMode = 'image',
) {
  const lines: ConnectorLine[] = []

  matchLayout.forEach((roundLayouts, roundIndex) => {
    if (roundIndex >= matchLayout.length - 1) return

    roundLayouts.forEach((layout, matchIndex) => {
      const target = matchLayout[roundIndex + 1]?.[Math.floor(matchIndex / 2)]
      if (!target) return

      const currentLeft = columnOffsets[layout.columnIndex - 1] ?? 0
      const targetLeft = columnOffsets[target.columnIndex - 1] ?? 0
      const currentWidth =
        columnWidths[layout.columnIndex - 1] ?? BRACKET_COLUMN_WIDTH
      const targetWidth =
        columnWidths[target.columnIndex - 1] ?? BRACKET_COLUMN_WIDTH
      const isFinalRound = roundIndex + 1 === matchLayout.length - 1
      const useHorizontalFinal = isFinalRound && structure !== 'single'

      const goingRight = target.columnIndex > layout.columnIndex
      const currentEdgeInset = getHorizontalSlotEdgeInset(
        currentWidth,
        displayMode,
      )
      const targetEdgeInset = getHorizontalSlotEdgeInset(
        targetWidth,
        displayMode,
      )
      const finalSlotEdgeOffset = getHorizontalFinalSlotEdgeOffset(displayMode)
      const xStart = goingRight
        ? currentLeft + currentWidth - currentEdgeInset
        : currentLeft + currentEdgeInset
      const xTarget = useHorizontalFinal
        ? goingRight
          ? targetLeft +
            finalSlotEdgeOffset -
            VERTICAL_STACKED_FINAL_CONNECTOR_GAP
          : targetLeft +
            targetWidth -
            finalSlotEdgeOffset +
            VERTICAL_STACKED_FINAL_CONNECTOR_GAP
        : goingRight
          ? targetLeft + targetEdgeInset
          : targetLeft + targetWidth - targetEdgeInset

      const delta = xTarget - xStart
      const travel = Math.abs(delta)
      const safeTravel = Math.max(travel, 1)
      const maxStub = Math.max(20, safeTravel / 2) // More generous stub
      const stub = Math.min(maxStub, safeTravel * 0.5)

      const xMerge = goingRight ? xStart + stub : xStart - stub

      const boxTop =
        BRACKET_TOP_PADDING +
        (layout.startRow - 1) * (BRACKET_ROW_HEIGHT + BRACKET_ROW_GAP) +
        layout.translateY

      const boxHeight =
        layout.span * BRACKET_ROW_HEIGHT + (layout.span - 1) * BRACKET_ROW_GAP

      // Row gaps make pure 25% / 75% slot placement drift inward by gap / 4.
      // Apply the same correction to slots and connector math so advancing
      // lines meet the next round as a clean horizontal segment.
      const yHome = getHorizontalSlotCenterY(
        boxTop,
        boxHeight,
        'home',
        displayMode,
      )
      const yAway = getHorizontalSlotCenterY(
        boxTop,
        boxHeight,
        'away',
        displayMode,
      )
      const yFork = (yHome + yAway) / 2

      // Calculate Target Y logic
      const targetBoxTop =
        BRACKET_TOP_PADDING +
        (target.startRow - 1) * (BRACKET_ROW_HEIGHT + BRACKET_ROW_GAP) +
        target.translateY
      const targetBoxHeight =
        target.span * BRACKET_ROW_HEIGHT + (target.span - 1) * BRACKET_ROW_GAP

      const feedTop = matchIndex % 2 === 0

      let yTarget: number

      if (useHorizontalFinal) {
        // Horizontal final slots sit on the centerline, with the team name
        // below the image. Aim at the visual center of the image, not the label.
        const finalCenterOffset =
          displayMode === 'text' ? 0 : -IMAGE_CENTER_OFFSET
        const yCenter = targetBoxTop + targetBoxHeight / 2

        yTarget = yCenter + finalCenterOffset
      } else {
        // Normal Vertical Layout (25% / 75%)
        // Target is the Home (25%) or Away (75%) slot of the target match
        yTarget = getHorizontalSlotCenterY(
          targetBoxTop,
          targetBoxHeight,
          feedTop ? 'home' : 'away',
          displayMode,
        )
      }

      // Orthogonal path for the bracket arms.
      // When source and target are aligned, keep the advance as one horizontal line.
      const xMid = (xMerge + xTarget) / 2
      const nextRoundConnector =
        Math.abs(yFork - yTarget) < 0.5
          ? `M${xMerge} ${yFork} H${xTarget}`
          : `M${xMerge} ${yFork} H${xMid} V${yTarget} H${xTarget}`

      const d = [
        `M${xStart} ${yHome} H${xMerge}`,
        `M${xStart} ${yAway} H${xMerge}`,
        `M${xMerge} ${yHome} V${yAway}`,
        nextRoundConnector,
      ].join(' ')

      lines.push({ d })
    })
  })

  return lines
}

export function buildBracketConnectors(
  matchLayout: PositionedMatch[][],
  columnOffsets: number[],
  columnWidths: number[],
  structure: BracketStructure,
  championColumnIndex: number | null,
  displayMode: BracketDisplayMode = 'image',
) {
  const lines = buildConnectors(
    matchLayout,
    columnOffsets,
    columnWidths,
    structure,
    displayMode,
  )

  if (!championColumnIndex) return lines

  const finalLayout = matchLayout.at(-1)?.[0]
  if (!finalLayout) return lines

  const currentLeft = columnOffsets[finalLayout.columnIndex - 1] ?? 0
  const currentWidth =
    columnWidths[finalLayout.columnIndex - 1] ?? BRACKET_COLUMN_WIDTH
  const targetLeft =
    columnOffsets[championColumnIndex - 1] ??
    currentLeft + currentWidth + BRACKET_COLUMN_GAP

  const currentEdgeInset = getHorizontalSlotEdgeInset(currentWidth, displayMode)
  const xStart = currentLeft + currentWidth - currentEdgeInset
  const xTarget = targetLeft + CHAMPION_CONNECTOR_OFFSET
  const delta = xTarget - xStart
  const travel = Math.abs(delta)
  const safeTravel = Math.max(travel, 1)
  const maxStub = Math.max(20, safeTravel / 2)
  const stub = Math.min(maxStub, safeTravel * 0.5)
  const xMerge = xStart + stub
  const xMid = (xMerge + xTarget) / 2

  const boxTop =
    BRACKET_TOP_PADDING +
    (finalLayout.startRow - 1) * (BRACKET_ROW_HEIGHT + BRACKET_ROW_GAP) +
    finalLayout.translateY
  const boxHeight =
    finalLayout.span * BRACKET_ROW_HEIGHT +
    (finalLayout.span - 1) * BRACKET_ROW_GAP

  const yHome = getHorizontalSlotCenterY(boxTop, boxHeight, 'home', displayMode)
  const yAway = getHorizontalSlotCenterY(boxTop, boxHeight, 'away', displayMode)
  const yFork = (yHome + yAway) / 2
  const yTarget = finalLayout.centerY

  lines.push({
    d: [
      `M${xStart} ${yHome} H${xMerge}`,
      `M${xStart} ${yAway} H${xMerge}`,
      `M${xMerge} ${yHome} V${yAway}`,
      `M${xMerge} ${yFork} H${xMid} V${yTarget} H${xTarget}`,
    ].join(' '),
  })

  return lines
}

export function getVerticalMatchBox(
  layout: PositionedMatch,
  columnOffsets: number[],
  columnWidths: number[],
  xOffset: number,
  yAxisLength: number,
  reverseY: boolean,
) {
  return transposeBox(
    getMatchBox(layout, columnOffsets, columnWidths),
    xOffset,
    yAxisLength,
    reverseY,
  )
}

export function getVerticalChampionBox(
  placement: ChampionPlacement,
  columnOffsets: number[],
  columnWidths: number[],
  xOffset: number,
  yAxisLength: number,
  reverseY: boolean,
) {
  return transposeBox(
    getChampionBox(placement, columnOffsets, columnWidths),
    xOffset,
    yAxisLength,
    reverseY,
  )
}

export function getVerticalSlotX(box: BracketBox, side: WinnerSide) {
  return (
    box.left +
    box.width * (side === 'home' ? 0.25 : 0.75) +
    getVerticalSlotCenterOffset(side)
  )
}

export function getVerticalSlotY(box: BracketBox, goingDown: boolean) {
  return (
    box.top +
    box.height / 2 +
    (goingDown ? VERTICAL_SLOT_EDGE_OFFSET : -VERTICAL_SLOT_EDGE_OFFSET)
  )
}

export function buildVerticalAdvancePath(
  box: BracketBox,
  targetBox: BracketBox,
  targetSide: WinnerSide,
  targetMode: 'standard' | 'stacked-final' = 'standard',
  displayMode: BracketDisplayMode = 'image',
) {
  const goingDown = targetBox.top > box.top
  const direction = goingDown ? 1 : -1
  const xHome = getVerticalSlotX(box, 'home')
  const xAway = getVerticalSlotX(box, 'away')
  const xFork = (xHome + xAway) / 2
  const slotEdgeOffset = getVerticalSlotEdgeOffset(displayMode)
  const yStart =
    box.top + box.height / 2 + (goingDown ? slotEdgeOffset : -slotEdgeOffset)
  const targetX =
    targetMode === 'stacked-final'
      ? targetBox.left + targetBox.width / 2
      : getVerticalSlotX(targetBox, targetSide)
  const targetY =
    targetMode === 'stacked-final'
      ? targetSide === 'home'
        ? targetBox.top - VERTICAL_STACKED_FINAL_CONNECTOR_GAP
        : targetBox.top +
          targetBox.height +
          VERTICAL_STACKED_FINAL_CONNECTOR_GAP
      : targetBox.top +
        targetBox.height / 2 +
        (!goingDown ? slotEdgeOffset : -slotEdgeOffset)
  const travel = Math.abs(targetY - yStart)
  const stub = Math.min(Math.max(28, travel * 0.35), VERTICAL_SLOT_EDGE_OFFSET)
  const yMerge = yStart + direction * stub
  const yMid = (yMerge + targetY) / 2
  const nextRoundConnector =
    Math.abs(xFork - targetX) < 0.5
      ? `M${xFork} ${yMerge} V${targetY}`
      : `M${xFork} ${yMerge} V${yMid} H${targetX} V${targetY}`

  return [
    `M${xHome} ${yStart} V${yMerge}`,
    `M${xAway} ${yStart} V${yMerge}`,
    `M${xHome} ${yMerge} H${xAway}`,
    nextRoundConnector,
  ].join(' ')
}

export function buildVerticalChampionPath(
  box: BracketBox,
  championBox: BracketBox,
  displayMode: BracketDisplayMode = 'image',
) {
  const goingDown = championBox.top > box.top
  const direction = goingDown ? 1 : -1
  const xHome = getVerticalSlotX(box, 'home')
  const xAway = getVerticalSlotX(box, 'away')
  const xFork = (xHome + xAway) / 2
  const slotEdgeOffset = getVerticalSlotEdgeOffset(displayMode)
  const yStart =
    box.top + box.height / 2 + (goingDown ? slotEdgeOffset : -slotEdgeOffset)
  const targetX = championBox.left + championBox.width / 2
  const targetY = goingDown
    ? championBox.top
    : championBox.top + championBox.height
  const travel = Math.abs(targetY - yStart)
  const stub = Math.min(Math.max(28, travel * 0.35), VERTICAL_SLOT_EDGE_OFFSET)
  const yMerge = yStart + direction * stub
  const yMid = (yMerge + targetY) / 2
  const championConnector =
    Math.abs(xFork - targetX) < 0.5
      ? `M${xFork} ${yMerge} V${targetY}`
      : `M${xFork} ${yMerge} V${yMid} H${targetX} V${targetY}`

  return [
    `M${xHome} ${yStart} V${yMerge}`,
    `M${xAway} ${yStart} V${yMerge}`,
    `M${xHome} ${yMerge} H${xAway}`,
    championConnector,
  ].join(' ')
}

export function buildVerticalBracketConnectors(
  matchLayout: PositionedMatch[][],
  columnOffsets: number[],
  columnWidths: number[],
  championPlacement: ChampionPlacement | null,
  xOffset: number,
  yAxisLength: number,
  reverseY: boolean,
  displayMode: BracketDisplayMode = 'image',
) {
  const lines: ConnectorLine[] = []

  matchLayout.forEach((roundLayouts, roundIndex) => {
    if (roundIndex >= matchLayout.length - 1) return

    roundLayouts.forEach((layout, matchIndex) => {
      const target = matchLayout[roundIndex + 1]?.[Math.floor(matchIndex / 2)]
      if (!target) return

      const box = getVerticalMatchBox(
        layout,
        columnOffsets,
        columnWidths,
        xOffset,
        yAxisLength,
        reverseY,
      )
      const targetBox = getVerticalMatchBox(
        target,
        columnOffsets,
        columnWidths,
        xOffset,
        yAxisLength,
        reverseY,
      )
      const targetSide = matchIndex % 2 === 0 ? 'home' : 'away'
      const isTargetFinalRound = roundIndex + 1 === matchLayout.length - 1
      const targetMode =
        isTargetFinalRound && !championPlacement ? 'stacked-final' : 'standard'

      lines.push({
        d: buildVerticalAdvancePath(
          box,
          targetBox,
          targetSide,
          targetMode,
          displayMode,
        ),
      })
    })
  })

  if (championPlacement) {
    const finalLayout = matchLayout.at(-1)?.[0]
    if (finalLayout) {
      const finalBox = getVerticalMatchBox(
        finalLayout,
        columnOffsets,
        columnWidths,
        xOffset,
        yAxisLength,
        reverseY,
      )
      const championBox = getVerticalChampionBox(
        championPlacement,
        columnOffsets,
        columnWidths,
        xOffset,
        yAxisLength,
        reverseY,
      )

      lines.push({
        d: buildVerticalChampionPath(finalBox, championBox, displayMode),
      })
    }
  }

  return lines
}

export function buildColumnHeaders(
  matchLayout: PositionedMatch[][],
  columnCount: number,
  roundsLength: number,
  structure: BracketStructure,
  championColumnIndex: number | null,
  labels: {
    champion: string
    final: string
    round: (round: number) => string
  },
): ColumnHeader[] {
  if (!roundsLength) return []

  const counts = new Map<number, number>()
  matchLayout.forEach((roundLayouts) => {
    roundLayouts.forEach((layout) => {
      counts.set(layout.columnIndex, (counts.get(layout.columnIndex) ?? 0) + 1)
    })
  })

  const headers: ColumnHeader[] = Array.from(
    { length: columnCount },
    (_, index) => {
      const columnIndex = index + 1
      const relativeColumn =
        structure === 'single'
          ? columnIndex - 1
          : columnIndex - 1 - (roundsLength - 1)

      const roundIndex =
        structure === 'single'
          ? relativeColumn
          : roundsLength - 1 - Math.abs(relativeColumn)

      return {
        columnIndex,
        label:
          roundIndex === roundsLength - 1
            ? labels.final
            : labels.round(roundIndex + 1),
        matchCount: counts.get(columnIndex) ?? 0,
        roundIndex,
      }
    },
  )

  if (championColumnIndex) {
    headers.push({
      columnIndex: championColumnIndex,
      label: labels.champion,
      matchCount: 0,
      roundIndex: roundsLength,
      isChampion: true,
    })
  }

  return headers
}

export function getChampionPlacement(
  matchLayout: PositionedMatch[][],
  championColumnIndex: number | null,
): ChampionPlacement | null {
  if (!championColumnIndex) return null

  const finalLayout = matchLayout.at(-1)?.[0]
  if (!finalLayout) return null

  return {
    columnIndex: championColumnIndex,
    rowStart: finalLayout.startRow,
    rowEnd: finalLayout.startRow + finalLayout.span,
  }
}
